import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    IconButton,
    Badge,
    Popover,
    Box,
    Typography,
    List,
    ListItem,
    ListItemText,
    Divider,
    Button,
    Avatar,
    CircularProgress,
    Tooltip,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import FiberNewIcon from "@mui/icons-material/FiberNew";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import axiosInstance from "../services/axiosInstance";
import apiEndPointsConfig from "../services/apiEndPoints";
import { useNavigate } from "react-router-dom";

const SIREN_SOUND_URL = "https://assets.mixkit.co/active_storage/sfx/991/991-preview.mp3";

const NotificationCenter = () => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [isAudioEnabled, setIsAudioEnabled] = useState(() => {
        return localStorage.getItem("globalNotificationEnabled") === "true";
    });
    const lastIdRef = useRef(null);
    const notificationsRef = useRef([]);
    const navigate = useNavigate();
    const audioRef = useRef(new Audio(SIREN_SOUND_URL));
    const sirenTimeoutRef = useRef(null);

    const playSiren = useCallback(() => {
        if (isAudioEnabled) {
            console.log("🔥 SIREN! Danger lead detected (Playing for 10s).");

            // Clear existing timeout if any
            if (sirenTimeoutRef.current) clearTimeout(sirenTimeoutRef.current);

            audioRef.current.currentTime = 0;
            audioRef.current.loop = true; // Loop for the duration
            audioRef.current.play().catch(err => {
                console.error("🔔 Audio play blocked by browser:", err);
            });

            // Stop after exactly 10 seconds
            sirenTimeoutRef.current = setTimeout(() => {
                audioRef.current.pause();
                audioRef.current.currentTime = 0;
                console.log("🔇 Siren auto-stopped after 10s.");
            }, 10000);
        }
    }, [isAudioEnabled]);

    useEffect(() => {
        localStorage.setItem("globalNotificationEnabled", isAudioEnabled);
    }, [isAudioEnabled]);

    const fetchGlobalLeads = useCallback(async (isSilent = false) => {
        if (!isAudioEnabled && isSilent) return;

        try {
            if (!isSilent) setLoading(true);
            const res = await axiosInstance.get(apiEndPointsConfig.getGlobalLatestLeads || "/campaigns/global/latest");

            if (res.data.success) {
                const leads = res.data.data || [];

                if (leads.length > 0) {
                    const currentLatestId = leads[0]._id;
                    console.log(`🔍 Polling: Latest ID ${currentLatestId}, Known ID ${lastIdRef.current}`);

                    if (lastIdRef.current && currentLatestId !== lastIdRef.current) {
                        const isTrulyNew = !notificationsRef.current.find(n => n._id === currentLatestId);

                        if (isTrulyNew) {
                            setUnreadCount(prev => prev + 1);
                            playSiren();
                        }
                    }

                    lastIdRef.current = currentLatestId;
                    notificationsRef.current = leads;
                    setNotifications(leads);
                }
            }
        } catch (err) {
            console.error("Error fetching global notifications:", err);
        } finally {
            if (!isSilent) setLoading(false);
        }
    }, [playSiren]);

    useEffect(() => {
        let intervalId = null;

        if (isAudioEnabled) {
            fetchGlobalLeads();
            intervalId = setInterval(() => {
                fetchGlobalLeads(true);
            }, 60000);
        } else {
            // Clean up state when monitoring is turned off
            setNotifications([]);
            setUnreadCount(0);
            lastIdRef.current = null;
        }

        return () => {
            if (intervalId) clearInterval(intervalId);
            if (sirenTimeoutRef.current) clearTimeout(sirenTimeoutRef.current);
            audioRef.current.pause(); // Ensure silent on unmount
        };
    }, [isAudioEnabled, fetchGlobalLeads]);

    const testAudio = () => {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(e => alert("Please click anywhere on the page first to enable audio."));
    };

    const handleOpen = (event) => {
        setAnchorEl(event.currentTarget);
        setUnreadCount(0);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    const formatTime = (date) => {
        return new Date(date).toLocaleString("en-IN", {
            hour: "2-digit",
            minute: "2-digit",
            day: "2-digit",
            month: "short",
        });
    };

    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Tooltip title={isAudioEnabled ? "Mute Alerts" : "Unmute Alerts"}>
                <IconButton onClick={() => setIsAudioEnabled(!isAudioEnabled)} color="inherit" size="small">
                    {isAudioEnabled ? <VolumeUpIcon fontSize="small" /> : <VolumeOffIcon fontSize="small" />}
                </IconButton>
            </Tooltip>

            <IconButton color="inherit" onClick={handleOpen}>
                <Badge badgeContent={unreadCount} color="error" overlap="circular">
                    <NotificationsIcon />
                </Badge>
            </IconButton>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                PaperProps={{
                    sx: {
                        width: 360,
                        maxHeight: 500,
                        borderRadius: 3,
                        boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
                        mt: 1.5,
                    },
                }}
            >
                <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center", bgcolor: "#f8fafc" }}>
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Global Lead Alerts</Typography>
                        <Button size="small" onClick={testAudio} sx={{ fontSize: "0.6rem", mt: -0.5, p: 0, minWidth: 0, textTransform: "none" }}>
                            Test Siren
                        </Button>
                    </Box>
                    {loading && <CircularProgress size={16} />}
                </Box>
                <Divider />
                <List sx={{ p: 0 }}>
                    {notifications.length === 0 ? (
                        <Box sx={{ p: 4, textAlign: "center" }}>
                            <Typography variant="body2" color="text.secondary">No recent leads found.</Typography>
                        </Box>
                    ) : (
                        notifications.map((lead, index) => (
                            <React.Fragment key={lead._id}>
                                <ListItem
                                    alignItems="flex-start"
                                    sx={{
                                        "&:hover": { bgcolor: "#f1f5f9" },
                                        cursor: "pointer",
                                        bgcolor: index < unreadCount ? "rgba(30, 58, 138, 0.05)" : "inherit"
                                    }}
                                    onClick={() => {
                                        navigate("/agent/campaign-sync");
                                        handleClose();
                                    }}
                                >
                                    <Avatar sx={{ bgcolor: "#1e293b", mr: 2, width: 32, height: 32, fontSize: "0.8rem" }}>
                                        {lead.name ? lead.name[0] : "L"}
                                    </Avatar>
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                                                <Typography variant="body2" sx={{ fontWeight: 700 }}>{lead.name || "Unknown Lead"}</Typography>
                                                <Typography variant="caption" color="text.secondary">{formatTime(lead.createdAt)}</Typography>
                                            </Box>
                                        }
                                        secondary={
                                            <Box sx={{ mt: 0.5 }}>
                                                <Typography variant="caption" display="block" color="primary" sx={{ fontWeight: 600 }}>
                                                    {lead.userId?.companyName || "Service Provider"}
                                                </Typography>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                                                    <Chip label={lead.source || "Sheet"} size="small" sx={{ height: 16, fontSize: "0.6rem" }} />
                                                    <Typography variant="caption">{lead.phoneNumber}</Typography>
                                                </Box>
                                            </Box>
                                        }
                                    />
                                </ListItem>
                                {index < notifications.length - 1 && <Divider component="li" />}
                            </React.Fragment>
                        ))
                    )}
                </List>
                <Divider />
                <Box sx={{ p: 1, textAlign: "center" }}>
                    <Button size="small" fullWidth onClick={() => { navigate("/agent/campaign-sync"); handleClose(); }}>
                        View All Campaigns
                    </Button>
                </Box>
            </Popover>
        </Box>
    );
};

// Simple Chip substitute since we are in a tight spot
const Chip = ({ label, size, sx }) => (
    <Box sx={{
        display: "inline-flex",
        px: 1,
        py: 0.2,
        borderRadius: 1,
        bgcolor: "#e2e8f0",
        fontSize: "0.7rem",
        ...sx
    }}>
        {label}
    </Box>
);

export default NotificationCenter;
